import { useEffect, useState } from 'react';
import { supabase, getCurrentUser } from './supabase';
import type { UserProfile } from '../types';
import { useDemo } from './demoContext';
import { DEMO_PROFILE } from './demoData';

/* ── Profile + Role hook ─────────────────────────────────────────────────────
 * Returns the current user's profile (including role) from Supabase.
 * Re-fetches whenever auth state changes.
 * ──────────────────────────────────────────────────────────────────────────── */

interface UseProfileResult {
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isClinician: boolean;
}

export function useProfile(): UseProfileResult {
  const demo = useDemo();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (demo) {
      setProfile(DEMO_PROFILE);
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      const user = await getCurrentUser();
      if (!user || cancelled) { setLoading(false); return; }

      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, dob, oura_token, role, created_at')
        .eq('id', user.id)
        .maybeSingle();

      if (!cancelled) {
        if (data && !error) {
          setProfile(data as UserProfile);
        }
        setLoading(false);
      }
    }

    load();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      load();
    });

    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  return {
    profile,
    loading,
    isAdmin: profile?.role === 'admin',
    isClinician: profile?.role === 'clinician' || profile?.role === 'admin',
  };
}
